import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';

export type CachedOpportunityStageBadge = {
    label: string;
    color: string;
    title: string;
};

/** `undefined` = not loaded yet; `null` = loaded, no opportunity; object = badge payload */
export type OpportunityStageCacheLookup = CachedOpportunityStageBadge | null | undefined;

type OpportunityStageCacheContextValue = {
    get: (agentId: string, customerId: string) => OpportunityStageCacheLookup;
    set: (agentId: string, customerId: string, value: CachedOpportunityStageBadge | null) => void;
    /** Internal: bumps when cache mutates so Provider value identity changes */
    _cacheVersion: number;
};

const OpportunityStageCacheContext = createContext<OpportunityStageCacheContextValue | null>(null);

/**
 * In-memory cache for opportunity-stage badges while this list is mounted.
 * Clears when `ThreadsList` unmounts (e.g. leaving Messages); avoids Virtuoso
 * refetch churn without a tab-wide module cache.
 */
export const OpportunityStageCacheProvider = ({ children }: { children: ReactNode }) => {
    const mapRef = useRef(new Map<string, CachedOpportunityStageBadge | null>());
    const [generation, setGeneration] = useState(0);

    const get = useCallback((agentId: string, customerId: string): OpportunityStageCacheLookup => {
        if (!agentId || !customerId) {
            return undefined;
        }
        const key = `${agentId}::${customerId}`;
        if (!mapRef.current.has(key)) {
            return undefined;
        }
        return mapRef.current.get(key) ?? null;
    }, []);

    const set = useCallback(
        (agentId: string, customerId: string, value: CachedOpportunityStageBadge | null) => {
            if (!agentId || !customerId) {
                return;
            }
            mapRef.current.set(`${agentId}::${customerId}`, value);
            setGeneration((g) => g + 1);
        },
        [],
    );

    const value = useMemo(
        () => ({ get, set, _cacheVersion: generation }),
        [get, set, generation],
    );

    return (
        <OpportunityStageCacheContext.Provider value={value}>
            {children}
        </OpportunityStageCacheContext.Provider>
    );
};

export const useOpportunityStageCache = (): OpportunityStageCacheContextValue => {
    const ctx = useContext(OpportunityStageCacheContext);
    if (!ctx) {
        throw new Error('useOpportunityStageCache must be used within OpportunityStageCacheProvider');
    }
    return ctx;
};
